// src/theme/TenantThemeProvider.js
import React from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { useTenant } from '../context/TenantContext';

// Create MUI theme from tenant colors
// This comprehensive theme supports the full migration from hardcoded colors
const createTenantTheme = (tenantColors) => {
  // Primary color (tenant-customizable)
  const primaryMain = tenantColors?.primary || '#1976d2';
  const primaryDark = tenantColors?.primaryDark || '#1565c0';
  const primaryLight = tenantColors?.primaryLight || '#42a5f5';

  // Secondary color (tenant-customizable)
  const secondaryMain = tenantColors?.secondary || '#4CAF50';

  return createTheme({
    palette: {
      // Primary - tenant brand color (buttons, links, accents)
      primary: {
        main: primaryMain,
        dark: primaryDark,
        light: primaryLight,
        contrastText: '#ffffff'
      },
      // Secondary - accent color
      secondary: {
        main: secondaryMain,
        dark: '#388e3c',
        light: '#81c784',
        contrastText: '#ffffff'
      },
      // Error/Danger - red tones
      error: {
        main: '#dc2626',
        dark: '#991b1b',
        light: '#f87171',
        contrastText: '#ffffff'
      },
      // Warning - amber/orange tones
      warning: {
        main: '#f59e0b',
        dark: '#92400e',
        light: '#fcd34d',
        contrastText: '#000000'
      },
      // Success - green tones
      success: {
        main: '#10b981',
        dark: '#065f46',
        light: '#6ee7b7',
        contrastText: '#ffffff'
      },
      // Info - blue tones
      info: {
        main: '#3b82f6',
        dark: '#1e40af',
        light: '#93c5fd',
        contrastText: '#ffffff'
      },
      // Grey palette (Tailwind-inspired, used throughout)
      grey: {
        50: '#f9fafb',
        100: '#f3f4f6',
        200: '#e5e7eb',
        300: '#d1d5db',
        400: '#9ca3af',
        500: '#6b7280',
        600: '#4b5563',
        700: '#374151',
        800: '#1f2937',
        900: '#111827'
      },
      // Text colors
      text: {
        primary: '#111827',    // grey.900
        secondary: '#6b7280',  // grey.500
        disabled: '#9ca3af'    // grey.400
      },
      // Background colors
      background: {
        default: '#f9fafb',    // grey.50 - page background
        paper: '#ffffff',      // white - cards, modals
        subtle: '#f3f4f6'      // grey.100 - subtle backgrounds
      },
      // Divider/border color
      divider: '#e5e7eb',      // grey.200
      // Custom colors for navigation (tenant-customizable)
      navigation: {
        background: tenantColors?.navBackground || '#333333',
        text: tenantColors?.navText || '#ffffff',
        activeIndicator: tenantColors?.navActiveIndicator || secondaryMain
      },
      // Status colors for data tables (not tenant-customizable - semantic meaning)
      status: {
        good: '#10b981',       // green
        warning: '#f59e0b',    // amber
        bad: '#ef4444',        // red
        neutral: '#6b7280'     // grey
      }
    },
    // Typography
    typography: {
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    },
    // Component overrides
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: '4px'
          }
        }
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }
        }
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-head': {
              backgroundColor: primaryMain,
              color: '#ffffff',
              fontWeight: 600
            }
          }
        }
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: '4px'
          }
        }
      }
    }
  });
};

export const TenantThemeProvider = ({ children }) => {
  const { colors } = useTenant();

  // Create theme from tenant colors
  const theme = React.useMemo(() => {
    return createTenantTheme(colors);
  }, [colors]);

  // Inject CSS custom properties for CSS files to use
  React.useEffect(() => {
    const root = document.documentElement;
    const palette = theme.palette;

    // Primary colors (tenant-customizable)
    root.style.setProperty('--color-primary', palette.primary.main);
    root.style.setProperty('--color-primary-dark', palette.primary.dark);
    root.style.setProperty('--color-primary-light', palette.primary.light);

    // Secondary colors (tenant-customizable)
    root.style.setProperty('--color-secondary', palette.secondary.main);
    root.style.setProperty('--color-secondary-dark', palette.secondary.dark);
    root.style.setProperty('--color-secondary-light', palette.secondary.light);

    // Navigation colors (tenant-customizable)
    root.style.setProperty('--color-nav-background', palette.navigation.background);
    root.style.setProperty('--color-nav-text', palette.navigation.text);
    root.style.setProperty('--color-nav-active', palette.navigation.activeIndicator);

    // Semantic colors (not tenant-customizable)
    root.style.setProperty('--color-error', palette.error.main);
    root.style.setProperty('--color-error-dark', palette.error.dark);
    root.style.setProperty('--color-error-light', palette.error.light);
    root.style.setProperty('--color-warning', palette.warning.main);
    root.style.setProperty('--color-warning-dark', palette.warning.dark);
    root.style.setProperty('--color-warning-light', palette.warning.light);
    root.style.setProperty('--color-success', palette.success.main);
    root.style.setProperty('--color-success-dark', palette.success.dark);
    root.style.setProperty('--color-success-light', palette.success.light);
    root.style.setProperty('--color-info', palette.info.main);
    root.style.setProperty('--color-info-dark', palette.info.dark);
    root.style.setProperty('--color-info-light', palette.info.light);

    // Grey palette
    root.style.setProperty('--color-grey-50', palette.grey[50]);
    root.style.setProperty('--color-grey-100', palette.grey[100]);
    root.style.setProperty('--color-grey-200', palette.grey[200]);
    root.style.setProperty('--color-grey-300', palette.grey[300]);
    root.style.setProperty('--color-grey-400', palette.grey[400]);
    root.style.setProperty('--color-grey-500', palette.grey[500]);
    root.style.setProperty('--color-grey-600', palette.grey[600]);
    root.style.setProperty('--color-grey-700', palette.grey[700]);
    root.style.setProperty('--color-grey-800', palette.grey[800]);
    root.style.setProperty('--color-grey-900', palette.grey[900]);

    // Text colors
    root.style.setProperty('--color-text-primary', palette.text.primary);
    root.style.setProperty('--color-text-secondary', palette.text.secondary);
    root.style.setProperty('--color-text-disabled', palette.text.disabled);

    // Background colors
    root.style.setProperty('--color-bg-default', palette.background.default);
    root.style.setProperty('--color-bg-paper', palette.background.paper);
    root.style.setProperty('--color-bg-subtle', palette.background.subtle);

    // Divider
    root.style.setProperty('--color-divider', palette.divider);
  }, [theme]);

  // Note: We render children even while loading to prevent flash
  // The theme will update when tenant loads
  return (
    <ThemeProvider theme={theme}>
      {children}
    </ThemeProvider>
  );
};

export default TenantThemeProvider;
